import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Download, Loader2 } from 'lucide-react';
import { trainingLogApi } from '../../services/trainingLogApi';
import type { MonthlyStatus, MonthlyStatusResponse } from '../../services/trainingLogApi';
import {
    MONTHLY_STATUSES, MONTHLY_STATUS_LABEL, downloadCsv, filterMonthlyStatus,
    monthlyStatusCounts, monthlyStatusCsv, monthlyStatusName,
} from '../../utils/trainingLogExport';

/** Current month as YYYY-MM in Europe/Zurich (never toISOString, which is UTC). */
function currentMonthZurich(): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit' })
        .format(new Date()).slice(0, 7);
}

/** The current month and the 12 before it, newest first. */
function monthOptions(current: string): { value: string; label: string }[] {
    const [y, m] = current.split('-').map(Number);
    return Array.from({ length: 13 }, (_, i) => {
        const d = new Date(Date.UTC(y, m - 1 - i, 1));
        return {
            value: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
        };
    });
}

const BADGE: Record<MonthlyStatus, string> = {
    training: 'bg-green-50 text-green-700',
    confirmed: 'bg-blue-50 text-blue-700',
    'no-response': 'bg-amber-50 text-amber-700',
    'not-reminded': 'bg-gray-100 text-gray-600',
};

const EXCLUSION_LABEL: Record<string, string> = {
    'private-address': 'Private address',
    'contract-type': 'Contract type',
    'opted-out': 'Opted out',
};

const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

/** HR/Admin: every active person for one month with training / confirmed / no-response / not-reminded. */
export function TrainingMonthlyStatus() {
    const current = useMemo(currentMonthZurich, []);
    const options = useMemo(() => monthOptions(current), [current]);
    const [month, setMonth] = useState(current);
    const [filter, setFilter] = useState<MonthlyStatus | 'all'>('all');
    const [data, setData] = useState<MonthlyStatusResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Only the newest request may write state: a late answer for an earlier month is dropped.
    const requestId = useRef(0);

    const load = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        // Clear the old month at once, so neither the table nor the CSV export can show it under the new month.
        setData(null);
        try {
            const result = await trainingLogApi.getMonthlyStatus(month);
            if (id === requestId.current) setData(result);
        } catch (e) {
            // An error is not an empty month: keep the two apart (repo learning "empty dropdown can be a 500").
            if (id === requestId.current) setError(e instanceof Error ? e.message : 'Request failed');
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [month]);

    useEffect(() => { void load(); }, [load]);

    const rows = data?.rows ?? [];
    const counts = useMemo(() => monthlyStatusCounts(rows), [rows]);
    const visible = useMemo(() => filterMonthlyStatus(rows, filter), [rows, filter]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label htmlFor="monthly-status-month" className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                    <select
                        id="monthly-status-month"
                        value={month}
                        onChange={e => { setMonth(e.target.value); setFilter('all'); }}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                    >
                        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="monthly-status-filter" className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select
                        id="monthly-status-filter"
                        value={filter}
                        onChange={e => setFilter(e.target.value as MonthlyStatus | 'all')}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                    >
                        <option value="all">All ({rows.length})</option>
                        {MONTHLY_STATUSES.map(s => (
                            <option key={s} value={s}>{MONTHLY_STATUS_LABEL[s]} ({counts[s]})</option>
                        ))}
                    </select>
                </div>
                <button
                    type="button"
                    disabled={loading || !data || visible.length === 0}
                    onClick={() => downloadCsv(monthlyStatusCsv(visible), `training-log-status-${month}.csv`)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
            )}

            {!loading && error && (
                <div role="alert" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="font-medium">The monthly overview could not be loaded.</div>
                        <div className="text-red-700">{error}</div>
                    </div>
                    <button type="button" onClick={() => void load()} className="px-3 py-1 border border-red-300 rounded-md bg-white text-red-800 hover:bg-red-100">
                        Retry
                    </button>
                </div>
            )}

            {!loading && !error && data && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {MONTHLY_STATUSES.map(s => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setFilter(filter === s ? 'all' : s)}
                                className={`text-left bg-white rounded-lg border p-3 hover:bg-gray-50 ${filter === s ? 'border-blue-500' : 'border-gray-200'}`}
                            >
                                <div className="text-xs text-gray-500">{MONTHLY_STATUS_LABEL[s]}</div>
                                <div className="text-2xl font-semibold text-gray-900">{counts[s]}</div>
                            </button>
                        ))}
                    </div>
                    <div className="text-xs text-gray-500">
                        {data.monthLabel}: {rows.length} active people, active on {data.activeOn}.
                    </div>

                    {rows.length === 0 ? (
                        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                            The server returned no employees for {data.monthLabel}. This is an answer, not an error.
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                            Nobody has the status "{filter === 'all' ? 'all' : MONTHLY_STATUS_LABEL[filter]}" in {data.monthLabel}.
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">Employee</th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">Company</th>
                                            <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                                            <th className="text-right px-4 py-3 font-medium text-gray-600">Trainings</th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">Reminded</th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">Confirmed</th>
                                            <th className="text-left px-4 py-3 font-medium text-gray-600">Not reminded because</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visible.map((r, i) => (
                                            <tr key={`${r.email}-${i}`} className="border-b border-gray-100 hover:bg-gray-50">
                                                <td className="px-4 py-3 text-gray-900 font-medium">
                                                    {monthlyStatusName(r)}
                                                    <div className="text-xs text-gray-400 font-normal">{r.email}</div>
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 uppercase">{r.companyCode ?? ''}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${BADGE[r.status]}`}>
                                                        {MONTHLY_STATUS_LABEL[r.status]}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right text-gray-900">{r.trainingCount}</td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(r.remindedAt)}</td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(r.confirmedAt)}</td>
                                                <td className="px-4 py-3 text-gray-600">{r.excludedReason ? EXCLUSION_LABEL[r.excludedReason] ?? r.excludedReason : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
